/**
 * Referral helpers — link building, email masking and short-lived storage of
 * an inbound referral code. All reward validation lives server-side.
 */
export const REFERRAL_BASE_URL = 'https://toxicdetector.com'
export const REFERRAL_STORAGE_KEY = 'toxicdetector.referral_code'
export const REFERRAL_CREDITS = 5
export function buildReferralUrl(code: string | null | undefined): string {
  if (!code) return `${REFERRAL_BASE_URL}/signup`
  return `${REFERRAL_BASE_URL}/signup?ref=${encodeURIComponent(code)}`
}
export function buildReferralMessage(url: string): string {
  return `Analyze your chat messages for toxic patterns and red flags with ToxicDetector AI. Use my link to get free scan credits: ${url}`
}
/**
 * Masks an email for display in the referral history: john@gmail.com → j***n@gmail.com
 */
export function maskEmail(email: string | null | undefined): string {
  if (!email || !email.includes('@')) return 'Hidden invite'
  const [local, domain] = email.split('@')
  if (local.length <= 2) return `${local.charAt(0)}***@${domain}`
  return `${local.charAt(0)}***${local.charAt(local.length - 1)}@${domain}`
}
export interface ShareTarget {
  id: 'whatsapp' | 'x' | 'linkedin' | 'email'
  label: string
  href: string
}
export function buildShareTargets(url: string): ShareTarget[] {
  const message = buildReferralMessage(url)
  return [
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      href: `https://wa.me/?text=${encodeURIComponent(message)}`,
    },
    {
      id: 'x',
      label: 'X',
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`,
    },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    },
    {
      id: 'email',
      label: 'Email',
      href: `mailto:?subject=${encodeURIComponent('Free ToxicDetector scan credits')}&body=${encodeURIComponent(message)}`,
    },
  ]
}
export function storeReferralCode(code: string): void {
  try {
    window.localStorage.setItem(REFERRAL_STORAGE_KEY, code)
  } catch {
    /* storage unavailable — referral simply is not persisted */
  }
}
export function getStoredReferralCode(): string | null {
  try {
    return window.localStorage.getItem(REFERRAL_STORAGE_KEY)
  } catch {
    return null
  }
}
export function clearStoredReferralCode(): void {
  try {
    window.localStorage.removeItem(REFERRAL_STORAGE_KEY)
  } catch {
    /* no-op */
  }
}
export async function copyToClipboard(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const input = document.createElement('textarea')
    input.value = value
    input.setAttribute('readonly', 'true')
    input.style.position = 'fixed'
    input.style.opacity = '0'
    document.body.appendChild(input)
    input.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(input)
    return ok
  } catch {
    return false
  }
}
